import { useEffect, useMemo, useState } from 'react';
import { useMailOrderStore } from '../../../../../stores/useMailOrderStore';
import { getStates } from '../../../../../services/listsApi';

interface StateOption {
  StateAbbr: string;
  StateName: string;
}

const ZIP_PATTERN = /^\d{5}$/;

function isAddressComplete(street1: string, city: string, state: string, zipCode: string): boolean {
  return street1.trim() !== '' && city.trim() !== '' && state !== '' && ZIP_PATTERN.test(zipCode);
}

function isPhoneComplete(areaCode: string, phoneNumber: string): boolean {
  return areaCode.trim().length >= 3 && phoneNumber.trim().length >= 7;
}

export interface WizardStep4DemographicsProps {
  customerServicePhone?: string;
}

export function WizardStep4Demographics({
  customerServicePhone = '',
}: WizardStep4DemographicsProps) {
  const { demographics, updateDemographicsPhone, updateDemographicsAddress } = useMailOrderStore();

  const [states, setStates] = useState<StateOption[]>([]);

  useEffect(() => {
    const cached = window.sessionStorage.getItem('statesList');
    if (cached) {
      try {
        setStates(JSON.parse(cached));
      } catch {
        /* ignore parse errors */
      }
    } else {
      getStates()
        .then(result => {
          if (result.StatusCode === 'SUCCESS' && Array.isArray(result.States)) {
            setStates(result.States as unknown as StateOption[]);
            window.sessionStorage.setItem('statesList', JSON.stringify(result.States));
          }
        })
        .catch(() => {});
    }
  }, []);

  const zipError = useMemo(() => {
    const z = demographics.address.zipCode;
    return z.length > 0 && !ZIP_PATTERN.test(z) ? 'ZIP code must be 5 digits.' : null;
  }, [demographics.address.zipCode]);

  const phoneComplete = isPhoneComplete(
    demographics.phone.areaCode,
    demographics.phone.phoneNumber
  );

  const addressComplete = isAddressComplete(
    demographics.address.street1,
    demographics.address.city,
    demographics.address.state,
    demographics.address.zipCode
  );

  const isStepValid = phoneComplete && addressComplete;

  return (
    <div>
      <h4 className="mb-1">Demographic Information</h4>
      <p className="text-muted mb-4">
        Please enter your current phone number and address using the form below.
      </p>

      <div className="row">
        {/* ── Left column: phone + info text ── */}
        <div className="col-12 col-md-6 mb-4 mb-md-0">
          <p className="fw-bold mb-2">Phone:</p>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              id="demoAreaCode"
              type="tel"
              className="form-control"
              maxLength={3}
              value={demographics.phone.areaCode}
              onChange={e => updateDemographicsPhone('areaCode', e.target.value.replace(/\D/g, ''))}
              placeholder="Area Code"
            />
            <input
              id="demoPhone"
              type="tel"
              className="form-control"
              maxLength={7}
              value={demographics.phone.phoneNumber}
              onChange={e =>
                updateDemographicsPhone('phoneNumber', e.target.value.replace(/\D/g, ''))
              }
              placeholder="Phone Number"
            />
            <input
              id="demoExt"
              type="tel"
              className="form-control"
              style={{ maxWidth: 90 }}
              maxLength={6}
              value={demographics.phone.extension}
              onChange={e =>
                updateDemographicsPhone('extension', e.target.value.replace(/\D/g, ''))
              }
              placeholder="Ext."
            />
          </div>

          <p className="mt-4">
            The information provided here will only be used in relation to home delivery
            prescriptions.
          </p>
          {customerServicePhone && (
            <p>
              If you have any questions about activating your home delivery account, please feel
              free to reach out to us at {customerServicePhone}.
            </p>
          )}

          {!isStepValid && (
            <p style={{ color: 'darkorange', fontStyle: 'italic', fontWeight: 'bold' }}>
              Please complete all fields.
            </p>
          )}
        </div>

        {/* ── Right column: address ── */}
        <div className="col-12 col-md-6">
          <p className="fw-bold mb-2">Address:</p>

          <div className="mb-2">
            <label htmlFor="demoStreet1" className="fw-bold d-block mb-1">
              Street 1
            </label>
            <input
              id="demoStreet1"
              type="text"
              className="form-control"
              value={demographics.address.street1}
              onChange={e => updateDemographicsAddress('street1', e.target.value)}
            />
          </div>

          <div className="mb-2">
            <label htmlFor="demoStreet2" className="fw-bold d-block mb-1">
              Street 2
            </label>
            <input
              id="demoStreet2"
              type="text"
              className="form-control"
              value={demographics.address.street2}
              onChange={e => updateDemographicsAddress('street2', e.target.value)}
            />
          </div>

          <div className="mb-2">
            <label htmlFor="demoCity" className="fw-bold d-block mb-1">
              City
            </label>
            <input
              id="demoCity"
              type="text"
              className="form-control"
              value={demographics.address.city}
              onChange={e => updateDemographicsAddress('city', e.target.value)}
            />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: '1 1 auto' }}>
              <label
                htmlFor="demoState"
                style={{ display: 'block', fontWeight: 'bold', marginBottom: 4 }}
              >
                State
              </label>
              <select
                id="demoState"
                className="form-select"
                value={demographics.address.state}
                onChange={e => updateDemographicsAddress('state', e.target.value)}
              >
                <option value="">&lt;Not Specified&gt;</option>
                {states
                  .filter(s => s.StateAbbr !== '')
                  .map(s => (
                    <option key={s.StateAbbr} value={s.StateAbbr}>
                      {s.StateName}
                    </option>
                  ))}
              </select>
            </div>
            <div style={{ flex: '0 0 120px' }}>
              <label
                htmlFor="demoZip"
                style={{ display: 'block', fontWeight: 'bold', marginBottom: 4 }}
              >
                Zip
              </label>
              <input
                id="demoZip"
                type="text"
                className={`form-control${zipError ? ' is-invalid' : ''}`}
                maxLength={5}
                value={demographics.address.zipCode}
                onChange={e =>
                  updateDemographicsAddress('zipCode', e.target.value.replace(/\D/g, ''))
                }
              />
              {zipError && <div className="invalid-feedback">{zipError}</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export { isAddressComplete, isPhoneComplete };
